import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "../icons";
import classNames from "classnames";
import { ThemeProviderContext } from "../ThemeProvider";
import { FloatingPortal, autoUpdate, flip, offset, useFloating, } from "@floating-ui/react";
import { textSizes } from "./TextSizeUtils";
export const TextSizeDropdown = ({ editor, defaultTextSize = "bodySmall", }) => {
    const theme = useContext(ThemeProviderContext);
    const [isOpen, setIsOpen] = useState(false);
    const [currentTextSizeLabel, setCurrentTextSizeLabel] = useState(theme.translate(textSizes[defaultTextSize].labelKey));
    useEffect(() => {
        const filterTextSizeClassNames = (classAttr) => {
            return classAttr
                .map((c) => (c === null || c === void 0 ? void 0 : c.split(" ")) || "")
                .flat()
                .filter((c) => c.startsWith("text-size-"));
        };
        const getTextSizeClassnamesWithinSelection = () => {
            // This gets a little complicated since a paragraph can have a text size that differs
            // from the text size of a span within it. One or both (or more) text sizes may be in
            // use within the current selection
            var _a;
            if (!editor)
                return [];
            const from = editor.view.state.selection.$from;
            const to = editor.view.state.selection.$to;
            const classAttrWithinSelection = [];
            const paragraphClassAttrWithinSelection = [];
            // If the sizedSpan is in the storedMarks list then it will be created and
            // wrapped around the next input, so that's the current text size selection
            const storedSizeSpan = (_a = editor.state.storedMarks) === null || _a === void 0 ? void 0 : _a.find((m) => m.type.name === "sizedSpan");
            if (storedSizeSpan) {
                classAttrWithinSelection.push(storedSizeSpan.attrs.class);
            }
            else {
                // Otherwise we loop through all the selected nodes
                editor.state.doc.nodesBetween(from.pos === to.pos ? from.pos - 1 : from.pos, to.pos, ({ type, marks, attrs }, _pos, parentNode) => {
                    if (type.name === "text") {
                        // If the text node is wrapped by a sizedSpan, then that has a text size class we care about
                        // Otherwise, that text size is determined by it's paragraph so we'll use that instead
                        const sizedSpan = marks.find(({ type }) => type.name === "sizedSpan");
                        classAttrWithinSelection.push(sizedSpan ? sizedSpan.attrs.class : parentNode === null || parentNode === void 0 ? void 0 : parentNode.attrs.class);
                    }
                    else if (type.name === "paragraph") {
                        paragraphClassAttrWithinSelection.push(attrs.class);
                    }
                });
                // If there were no text nodes selected, then just use the selected paragraph classes
                if (classAttrWithinSelection.length === 0) {
                    classAttrWithinSelection.push(...paragraphClassAttrWithinSelection);
                }
            }
            return [...new Set(filterTextSizeClassNames(classAttrWithinSelection))];
        };
        const getCurrentTextSizeLabelKey = () => {
            const textSizeClassnames = getTextSizeClassnamesWithinSelection();
            if (textSizeClassnames.length > 1)
                return "components.rich_text_editor.text_size.multiple_sizes";
            const currentTextSize = Object.values(textSizes).find(({ className }) => className === textSizeClassnames[0]) || textSizes[defaultTextSize];
            return currentTextSize.labelKey;
        };
        const determineCurrentTextSize = () => {
            setCurrentTextSizeLabel(theme.translate(getCurrentTextSizeLabelKey()));
        };
        editor === null || editor === void 0 ? void 0 : editor.on("selectionUpdate", determineCurrentTextSize);
        editor === null || editor === void 0 ? void 0 : editor.on("transaction", determineCurrentTextSize);
        return () => {
            editor === null || editor === void 0 ? void 0 : editor.off("selectionUpdate", determineCurrentTextSize);
            editor === null || editor === void 0 ? void 0 : editor.off("transaction", determineCurrentTextSize);
        };
    }, [defaultTextSize, editor, theme]);
    const { x, y, refs } = useFloating({
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: setIsOpen,
        strategy: "fixed",
        placement: "top",
        middleware: [offset({ mainAxis: 8, crossAxis: 30 }), flip()],
    });
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (refs.floating.current &&
                !refs.floating.current.contains(event.target) &&
                isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener("mousedown", handleClickOutside);
        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, refs.floating]);
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ menuZIndex }) => (_jsxs(_Fragment, { children: [_jsxs("button", { className: "flex p-1 gap-1 hover:bg-general-gray-quaternary focus:bg-general-gray-quaternary", onClick: (e) => {
                        e.preventDefault();
                        setIsOpen((isOpen) => !isOpen);
                    }, ref: refs.setReference, children: [_jsx("span", { className: "text-sm", children: currentTextSizeLabel }), isOpen ? _jsx(ChevronUpIcon, {}) : _jsx(ChevronDownIcon, {})] }), _jsx(FloatingPortal, { root: document.body, children: isOpen && (_jsx("div", { className: classNames("rounded drop-shadow dark:drop-shadow-dark bg-background-primary dark:bg-background-tertiary-darkmode dark:border dark:border-border-subdued-darkmode focus:outline-2 overflow-hidden"), style: {
                            position: "absolute",
                            top: y !== null && y !== void 0 ? y : "",
                            left: x !== null && x !== void 0 ? x : "",
                            zIndex: menuZIndex,
                        }, ref: refs.setFloating, children: _jsx("ul", { className: "flex flex-col bg-general-light-primary border-general-gray-tertiary w-[400px] rounded", children: Object.entries(textSizes).map(([textSizeClassName, { labelKey, styles }]) => (_jsxs("li", { className: classNames("flex items-baseline p-1 w-full hover:bg-cta-secondary-hover focus:bg-cta-secondary-pressed cursor-pointer", styles), onClick: () => {
                                    setIsOpen(false);
                                    editor === null || editor === void 0 ? void 0 : editor.chain().focus().setTextSize(textSizeClassName).run();
                                }, children: [_jsx("span", { children: theme.translate(labelKey) }), textSizeClassName === defaultTextSize && (_jsx("span", { className: "text-[14px] ml-0.5 text-text-subdued leading-[16px]", children: theme.translate("components.rich_text_editor.text_size.default") }))] }, textSizeClassName))) }) })) })] })) }));
};
//# sourceMappingURL=TextSizeDropdown.js.map